import { useCallback, useEffect, useRef, useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { DeleteRecordButton } from '../../components/RecordActions';
import { KeyValue, Pill, Field, SelectInput, NumberInput, TextArea, DateInput, TextInput } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { BIZ_ROLES } from '../../lib/permissions';
import { dateLabel, num, customerShort } from '../../lib/format';
import { openAuthenticatedPrint } from '../../lib/print';
import { AssetTraceabilityPage } from './AssetTraceabilityPage';
import { rolesOf } from '../../lib/permissions';
import type { RepairLedgerRow, RepairLedgerSummary, RepairLedgerResponse, AssetReturnRecord, Role, Customer, Item } from '../../lib/types';

// 送修工件（统一入口）
//
// 同一套工件事实，台账与溯源一页：
//   · 台账   = 受托工件全生命周期（主入口；关键字可搜序列号/客户/合同/工单/物料）
//   · 溯源   = 只读时间线

// 送修工件台账
//
// 为什么需要这个页面：送修的四个事实分落四张表（工件 / 维修履历 / 质检 / 工单），
// 此前互不相连，于是「工件已登记、检验也做完了，台账却查不到」。
// 本页把四段事实并成一行，并显式标出「缺哪一段关联」——
// 台账漏记比多记更难发现，所以宁可显示「待关联」也不把行藏起来。
//
// 新订单的合同、工单和质检关联由 QC / Integral 同步按订单明细与工件身份自动建立。
// 页面不再允许按客户和物料粗筛后手工猜工单；旧数据未能精确匹配时只提示核查。

const CYCLE_STATUS_LABEL: Record<string, { label: string; tone: 'neutral' | 'info' | 'warn' | 'ok' | 'bad' }> = {
  intake: { label: '已收件', tone: 'neutral' },
  in_production: { label: '维修中', tone: 'warn' },
  inspected: { label: '已检验', tone: 'info' },
  ready: { label: '待交货', tone: 'info' },
  delivered: { label: '已交货', tone: 'ok' },
  returned: { label: '已退回', tone: 'neutral' },
  closed: { label: '已关闭', tone: 'neutral' },
};

function cyclePill(status?: string | null) {
  if (!status) return <Pill tone="bad" label="未建履历" />;
  const s = CYCLE_STATUS_LABEL[status] ?? { label: status, tone: 'neutral' as const };
  return <Pill tone={s.tone} label={s.label} />;
}

interface ReturnableAssetOption {
  asset_id: string;
  customer_serial: string;
  internal_code?: string | null;
  service_cycle_id: string;
  version: number;
  item_code?: string | null;
  item_name_zh?: string | null;
  contract_number?: string | null;
  returnable_quantity: number;
}

/**
 * 退料 → 退货单（送修工件无法修复，退回客户）
 *
 * 边界（LOCKED）：客户送修工件是「受托保管」，不是公司库存 —— 不能报废，
 * 修不好只能退货给客户。这里走的正是 /api/asset-return 的 draft→confirmed→signed
 * 权威链：签收（sign）才写 'returned' 流水并扣减在厂数，与交货出库互斥。
 * 退货单版式沿用交货单（公司→客户），只是名称不同，由 core-api 打印 asset-return。
 *
 * 幂等：source_id 在面板挂载时生成一次，重复点击创建不会落多张草稿。
 */
export function ReturnPanel({ row, onChanged, allReturnable = false }: { row: Pick<RepairLedgerRow, 'asset_id' | 'cycle_id' | 'contract_id'>; onChanged: () => void; allReturnable?: boolean }) {
  const today = new Date().toISOString().slice(0, 10);
  const srcRef = useRef<string>('');
  if (!srcRef.current) {
    const rnd = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    srcRef.current = `ledger/${row.asset_id}/${row.cycle_id}/${rnd}`;
  }

  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState('');
  const [retDate, setRetDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [rec, setRec] = useState<AssetReturnRecord | null>(null);
  const [printing, setPrinting] = useState(false);
  const optionsUrl = allReturnable
    ? '/api/asset-return/returnable?standalone=1'
    : row.contract_id
    ? `/api/asset-return/returnable?contract_id=${encodeURIComponent(row.contract_id)}`
    : row.cycle_id ? `/api/asset-return/returnable?service_cycle_id=${encodeURIComponent(row.cycle_id)}` : null;
  const { data: returnableAssets, loading: loadingAssets, reload: reloadAssets } = useDetail<ReturnableAssetOption[]>(optionsUrl);
  const [selectedAssetId, setSelectedAssetId] = useState(row.asset_id || '');
  const selectedAsset = (returnableAssets ?? []).find((a) => a.asset_id === selectedAssetId);

  useEffect(() => {
    if (!selectedAsset && returnableAssets?.length) setSelectedAssetId(returnableAssets[0].asset_id);
  }, [selectedAsset, returnableAssets]);

  async function create() {
    if (!selectedAsset) { setErr('请选择已登记且可退回的工件'); return; }
    if (!reason.trim()) { setErr('退料原因必填：修不好退回客户，需记录原因'); return; }
    const q = Number(qty);
    if (!Number.isFinite(q) || q <= 0) { setErr('退料数量必须是大于 0 的数字'); return; }
    if (q > Number(selectedAsset.returnable_quantity) + 1e-9) { setErr('退料数量超过所选工件的可退数量'); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post<{ return: AssetReturnRecord; message?: string }>('/api/asset-return/create', {
        service_cycle_id: selectedAsset.service_cycle_id,
        asset_id: selectedAsset.asset_id,
        quantity: q,
        reason: reason.trim(),
        return_date: retDate || today,
        notes: notes.trim() || undefined,
        contract_id: row.contract_id || undefined,
        source_system: 'web-console',
        source_id: srcRef.current,
      });
      setRec(r.return);
      setMsg(r.message || `退货单已创建：${r.return.number}（草稿，签收时扣减在厂数）`);
      reloadAssets();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建退货单失败');
    } finally { setBusy(false); }
  }

  async function advance(next: 'confirm' | 'sign') {
    if (!rec) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post<{ return: AssetReturnRecord; message?: string }>(`/api/asset-return/${next}`, { return_id: rec.id });
      setRec(r.return);
      setMsg(r.message || (next === 'sign' ? '已签收归还，在厂数已扣减' : '已确认，待客户签收'));
      if (next === 'sign') onChanged(); // 在厂数变化，刷新台账
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(false); }
  }

  async function doPrint() {
    if (!rec) return;
    setPrinting(true); setErr(null);
    const r = await openAuthenticatedPrint('asset-return', rec.id);
    if (!r.ok) setErr(r.message || '打印失败');
    setPrinting(false);
  }

  const signed = rec?.status === 'signed';
  const confirmed = rec?.status === 'confirmed';
  const isDraft = rec?.status === 'draft';

  return (
    <div className="notice" style={{ display: 'block', background: 'var(--panel-inset)', marginTop: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>退料 / 退货单（工件无法修复，退回客户）</div>
      <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--ink-3)' }}>
        送修工件由我方受托保管，<b>不能报废、不计入公司库存</b>；确认无法修复时开一张退货单归还客户。
        签收（最后一步）才会写归还流水并扣减在厂数。
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="选择已登记的退回工件" span>
          <SelectInput value={selectedAssetId} onChange={(e) => { setSelectedAssetId(e.target.value); setQty('1'); }} disabled={!!rec || loadingAssets}>
            {loadingAssets ? <option value="">加载中…</option> : null}
            {!loadingAssets && !returnableAssets?.length ? <option value="">没有可退回的已登记工件</option> : null}
            {(returnableAssets ?? []).map((a) => (
              <option key={a.asset_id} value={a.asset_id}>
                {`${a.customer_serial} · ${a.item_code ?? ''} ${a.item_name_zh ?? ''} · V${a.version} · 可退 ${num(a.returnable_quantity, 0)}`}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="退料数量">
          <NumberInput value={qty} min={1} max={selectedAsset?.returnable_quantity} step={1} onChange={(e) => setQty(e.target.value)} disabled={!!rec || !selectedAsset} />
        </Field>
        <Field label="退料日期">
          <DateInput value={retDate} onChange={(e) => setRetDate(e.target.value)} disabled={!!rec} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 6 }}>
        <Field label="退料原因" span required>
          <TextArea value={reason} placeholder="如：激光管击穿，超出维修范围，退还原客户"
            onChange={(e) => setReason(e.target.value)} disabled={!!rec} />
        </Field>
        <Field label="备注" span>
          <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!!rec} />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        {!rec ? (
          <button className="btn" onClick={create} disabled={busy || !reason.trim() || !selectedAsset}>
            {busy ? '创建中…' : '创建退货单'}
          </button>
        ) : null}
        {rec && isDraft ? (
          <button className="btn" onClick={() => advance('confirm')} disabled={busy}>
            {busy ? '处理中…' : '确认退货单'}
          </button>
        ) : null}
        {rec && (isDraft || confirmed) ? (
          <button className="btn" onClick={() => advance('sign')} disabled={busy}>
            {busy ? '处理中…' : '签收归还（扣减在厂数）'}
          </button>
        ) : null}
        {rec ? (
          <button className="btn ghost" onClick={doPrint} disabled={printing}>
            {printing ? '打印中…' : '打印退货单'}
          </button>
        ) : null}
        {rec ? (
          <span className="num muted" style={{ fontSize: 12 }}>
            {rec.number} · {{ draft: '草稿', confirmed: '已确认', signed: '已签收', cancelled: '已取消' }[rec.status] ?? rec.status}
          </span>
        ) : null}
        {signed ? <Pill tone="ok" label="已归还·在厂数已扣减" /> : null}
      </div>

      {err ? <div className="notice bad" style={{ marginTop: 8 }}>{err}</div> : null}
      {msg ? <div className="notice" style={{ marginTop: 8 }}>{msg}</div> : null}
    </div>
  );
}

function RowDetail({ row, canReturn, onChanged }: { row: RepairLedgerRow; canReturn: boolean; onChanged: () => void }) {
  const insp = Number(row.inspection_count || 0);
  const acc = Number(row.inspection_accepted || 0);
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.customer_serial}</span>
        <h3>{`${row.item_code ?? ''} ${row.item_name_zh ?? ''}`.trim() || '—'}</h3>
        {cyclePill(row.cycle_status)}
        {row.pending_manual_link ? <Pill tone="bad" label="订单链待补齐" /> : null}
        <div className="spacer" />
      </div>

      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <KeyValue k="客户">{`${row.customer_code ?? ''} ${row.customer_name ?? ''}`.trim() || '—'}</KeyValue>
        <KeyValue k="内部编码">{row.internal_code || '—'}</KeyValue>
        <KeyValue k="工件状态">{row.asset_status || '—'}</KeyValue>
        <KeyValue k="收件日期">{dateLabel(row.first_received_date ?? row.intake_date)}</KeyValue>
        <KeyValue k="维修履历">{row.cycle_id ? `V${row.cycle_version} · ${row.cycle_status}` : '未建履历'}</KeyValue>
        <KeyValue k="返工次数">{num(row.rework_count, 0)}</KeyValue>
        <KeyValue k="所属合同">{row.contract_number || <span className="muted">未关联</span>}</KeyValue>
        <KeyValue k="关联工单">{row.work_order_number || <span className="muted">未关联</span>}</KeyValue>
        <KeyValue k="工单状态">{row.work_order_status || '—'}</KeyValue>
        <KeyValue k="已产出">{num(row.produced_quantity, 0)}</KeyValue>
        <KeyValue k="质检单">
          {insp ? (
            <>
              {`${insp} 张（合格 ${acc}`}
              {Number(row.inspection_rejected || 0) > 0 ? `，不合格 ${row.inspection_rejected}` : ''}
              {Number(row.inspection_pending || 0) > 0 ? `，待出结论 ${row.inspection_pending}` : ''}
              {'）'}
            </>
          ) : <span className="muted">无</span>}
        </KeyValue>
        <KeyValue k="最近检验">{dateLabel(row.last_inspection_date)}</KeyValue>
        <KeyValue k="交货单">
          {row.delivery_number ? `${row.delivery_number} · ${row.delivery_status}` : <span className="muted">未交货</span>}
        </KeyValue>
      </div>

      {row.cycle_id && row.cycle_status === 'intake' ? (
        <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>检验发现 / 维修建议</div>
      ) : null}
      {row.findings || row.recommendation ? (
        <div className="kv-grid" style={{ marginBottom: 8 }}>
          <KeyValue k="检验发现">{row.findings || '—'}</KeyValue>
          <KeyValue k="维修建议">{row.recommendation || '—'}</KeyValue>
        </div>
      ) : null}

      {row.cycle_id && row.pending_manual_link ? (
        <div className="notice warn">
          这条履历没有收到订单同步建立的工单关联。请检查 QC 订单、合同明细、工件序列号和同步记录；
          台账不再按客户和物料提供猜测式人工关联。
        </div>
      ) : null}

      {row.cycle_id && !row.pending_manual_link && !insp ? (
        <div className="notice">
          该工件的维修履历还没有任何质检单。若检验已在现场完成，需要把结论同步到统一核心，
          否则台账与工序状态都会停在「未检」。
        </div>
      ) : null}

      {row.cycle_id && row.cycle_status !== 'closed' ? (
        canReturn
          ? <ReturnPanel row={row} onChanged={onChanged} />
          : (
            <div className="notice" style={{ background: 'var(--panel-inset)', marginTop: 12 }}>
              退料 / 退货单：工件确认无法修复时归还客户（签收后扣减在厂数）。
              该操作需要退料权限，当前角色不可用（后端 /api/asset-return 写权限 = 生产·质检·仓储·销售）。
            </div>
          )
      ) : null}

      {row.cycle_id ? (
        <div className="row-actions" style={{ marginTop: 10 }}>
          {/* 管理员 + 业务操作员（BIZ_ROLES）可见，与后端路由放开范围一致：
              测试记录清理 = 删领退料流水 + 删维修版本，工程师独立完成整条链。 */}
          <DeleteRecordButton
            type="asset-service-cycles"
            id={row.cycle_id}
            label={`维修版本 V${row.cycle_version}`}
            minRoles={BIZ_ROLES}
            note={row.work_order_number ? `同时断开与工单 ${row.work_order_number} 的双向绑定，该工单随后可作废或删除。` : undefined}
            onDone={onChanged}
          />
        </div>
      ) : null}
      <div className="row-actions" style={{ marginTop: 10 }}>
        <DeleteRecordButton
          type="physical-assets"
          id={row.asset_id}
          label={`工件 ${row.customer_serial}`}
          note="删除工件会同时删除其维修版本。若工件已被工单、质检、退回或库存/交货单引用，系统会阻止删除并显示具体原因。"
          onDone={onChanged}
        />
      </div>
    </div>
  );
}

type WorkpieceTab = 'ledger' | 'trace';

export function RepairLedgerPage() {
  const [tab, setTab] = useState<WorkpieceTab>('ledger');
  return (
    <div>
      <div className="panel" style={{ marginBottom: 'var(--gap)', boxShadow: 'none' }}>
        <div className="panel-head" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <h2 style={{ marginRight: 8 }}>送修工件</h2>
          {([
            ['ledger', '台账'],
            ['trace', '溯源'],
          ] as [WorkpieceTab, string][]).map(([key, label]) => (
            <button
              key={key}
              className={`btn${tab === key ? ' primary' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
          <div className="spacer" />
          <div className="desc" style={{ margin: 0 }}>
            新系统登记的送修工件：台账可按序列号、客户或合同检索；序列号查询同时覆盖新系统与 ERPNext 历史记录。
          </div>
        </div>
      </div>
      {tab === 'ledger' ? <RepairLedgerInner /> : null}
      {tab === 'trace' ? <AssetTraceabilityPage /> : null}
    </div>
  );
}

export interface StandaloneRepairPiece { asset_id: string; cycle_id: string; customer_serial: string }

export function StandaloneRepairIntakeForm({ onCancel, onCreated }: { onCancel?: () => void; onCreated: (piece: StandaloneRepairPiece) => void }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [customerLabel, setCustomerLabel] = useState('');
  const [itemLabel, setItemLabel] = useState('');
  const [serial, setSerial] = useState('');
  const [assetType, setAssetType] = useState('');
  const [findings, setFindings] = useState('');
  const [intakeDate, setIntakeDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sourceId = useRef(`standalone-repair-intake/${globalThis.crypto?.randomUUID?.() ?? Date.now().toString(36)}`);

  useEffect(() => {
    Promise.all([
      api.list<Customer>('/api/customers', { limit: 500 }),
      api.list<Item>('/api/items', { limit: 500, active: 1 }),
    ]).then(([customerRows, itemRows]) => {
      setCustomers(customerRows);
      setItems(itemRows);
    }).catch((e) => setErr(e instanceof ApiError ? e.message : '加载客户和物料失败'));
  }, []);

  const customer = customers.find((c) => customerLabel === `${c.code} · ${c.legal_name}`);
  const item = items.find((i) => itemLabel === `${i.code} · ${i.name_zh}`);

  async function submit() {
    if (!customer) { setErr('请从客户列表中选择客户'); return; }
    if (!serial.trim()) { setErr('客户工件序列号必填'); return; }
    if (!findings.trim()) { setErr('请记录无法修复的原因'); return; }
    if (itemLabel && !item) { setErr('请从物料列表中选择物料，或清空物料栏'); return; }
    setBusy(true); setErr(null);
    try {
      const result = await api.post<{ data: { asset?: { id: string; service_cycles?: { id: string }[] }; resolution?: string | null; needs_resolution?: boolean; candidates?: unknown[] } }>('/api/physical-assets/intake', {
        customer_id: customer.id,
        item_id: item?.id || null,
        customer_serial: serial.trim(),
        asset_type: assetType.trim() || undefined,
        description: findings.trim(),
        findings: findings.trim(),
        recommendation: '无法修复，退回客户',
        first_received_date: intakeDate,
        intake_date: intakeDate,
        scenario: 'repair',
        cycle_status: 'intake',
        source_system: 'web-console-standalone-repair',
        source_id: sourceId.current,
      });
      const asset = result.data.asset;
      const cycleId = asset?.service_cycles?.[0]?.id;
      if (!asset?.id || !cycleId) {
        setErr(result.data.needs_resolution
          ? '该客户和序列号已有登记工件。请先在台账搜索该序列号：同一件工件可直接退货；确为另一件同号工件时，再补充区分信息后登记。'
          : '登记未完成，请检查工件是否已存在');
        return;
      }
      onCreated({ asset_id: asset.id, cycle_id: cycleId, customer_serial: serial.trim() });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '登记送修工件失败');
    } finally { setBusy(false); }
  }

  return (
    <div className="notice" style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>独立登记无法修复工件</div>
      <div className="desc" style={{ marginBottom: 10 }}>
        只登记客户、序列号和 V1 维修履历，并记录收件流水；不会创建 QC 销售订单、Integral 合同、工单或质检单。登记后可在台账该工件详情中开退货单。
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 10 }}>
        <Field label="客户" required>
          <TextInput list="repair-intake-customers" value={customerLabel} onChange={(e) => setCustomerLabel(e.target.value)} placeholder="输入客户代码或名称" disabled={busy} />
          <datalist id="repair-intake-customers">
            {customers.map((c) => <option key={c.id} value={`${c.code} · ${c.legal_name}`} />)}
          </datalist>
        </Field>
        <Field label="客户工件序列号" required>
          <TextInput value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="输入工件上的序列号" disabled={busy} />
        </Field>
        <Field label="物料（可选）">
          <TextInput list="repair-intake-items" value={itemLabel} onChange={(e) => setItemLabel(e.target.value)} placeholder="输入物料编码或名称" disabled={busy} />
          <datalist id="repair-intake-items">
            {items.map((i) => <option key={i.id} value={`${i.code} · ${i.name_zh}`} />)}
          </datalist>
        </Field>
        <Field label="工件类型（可选）">
          <TextInput value={assetType} onChange={(e) => setAssetType(e.target.value)} placeholder="如：阀杆、镜面闸板" disabled={busy} />
        </Field>
        <Field label="收件日期">
          <DateInput value={intakeDate} onChange={(e) => setIntakeDate(e.target.value)} disabled={busy} />
        </Field>
        <Field label="无法修复原因" required span>
          <TextArea value={findings} onChange={(e) => setFindings(e.target.value)} placeholder="记录检测发现及无法修复原因，退货单会单独填写退回原因" disabled={busy} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn primary" onClick={submit} disabled={busy || !customers.length}>
          {busy ? '登记中…' : '登记工件并进入台账'}
        </button>
        {onCancel ? <button className="btn ghost" onClick={onCancel} disabled={busy}>取消</button> : null}
      </div>
      {err ? <div className="notice bad" style={{ marginTop: 8 }}>{err}</div> : null}
    </div>
  );
}

function RepairLedgerInner() {
  const { user } = useAuth();
  // 退料 / 退货单写权限：与后端 /api/asset-return(create|confirm|sign) 角色对齐。
  const canReturn = (() => {
    // 统一身份角色经 rolesOf 展开后再判定（与后端 integral-role-map 同源）。
  const roles = rolesOf(user);
  return roles.includes('admin') || (['production', 'quality', 'warehouse', 'sales'] as Role[]).some((x) => roles.includes(x));
  })();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [summary, setSummary] = useState<RepairLedgerSummary | null>(null);
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [cycleStatus, setCycleStatus] = useState('');

  // 汇总与列表用同一个筛选口径，否则计数和列表会对不上（047/048 视图曾经踩过这个坑）
  const query = `limit=200${onlyUnlinked ? '&unlinked=1' : ''}${cycleStatus ? `&cycle_status=${cycleStatus}` : ''}`;
  const endpoint = `/api/physical-assets/repair-ledger?${query}`;

  // 只要计数，不重复拉 200 行（与 ListPage 的请求共用 where 条件）
  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get<RepairLedgerResponse>(`/api/physical-assets/repair-ledger?summary_only=1&${query}`);
      setSummary(r.summary);
    } catch { /* 汇总失败不阻断列表 */ }
  }, [query]);

  useEffect(() => { void loadSummary(); }, [loadSummary, reloadSignal]);

  const columns: Column<RepairLedgerRow>[] = [
    { header: '工件序列号', render: (r) => <span className="num strong">{r.customer_serial}</span> },
    { header: '客户', render: (r) => <span title={r.customer_name || ''}>{customerShort(r.customer_name)}</span> },
    { header: '物料', render: (r) => `${r.item_code ?? ''} ${r.item_name_zh ?? ''}`.trim() || '—' },
    { header: '维修履历', render: (r) => (r.cycle_id ? <Pill tone="info" label={`V${r.cycle_version}`} /> : <Pill tone="bad" label="未建履历" />) },
    { header: '履历状态', render: (r) => cyclePill(r.cycle_status) },
    { header: '所属合同', render: (r) => (r.contract_number ? <span className="num">{r.contract_number}</span> : <span className="muted">未关联</span>) },
    { header: '工单', render: (r) => (r.work_order_number ? <span className="num">{r.work_order_number}</span> : <span className="muted">未关联</span>) },
    {
      header: '质检',
      align: 'right',
      // 张数 / 合格 / 不合格必须分开显示：「检了 8 次、合格 8 次」在返修件上
      // 和一次过件长得一样，读表人会以为已完成。判过不合格的要显式标出来。
      render: (r) => {
        const n = Number(r.inspection_count || 0);
        if (!n) return <span className="muted">无</span>;
        const acc = Number(r.inspection_accepted || 0);
        const rej = Number(r.inspection_rejected || 0);
        const pend = Number(r.inspection_pending || 0);
        if (rej > 0) return <Pill tone="bad" label={`${acc}合格 / ${rej}不合格 (共${n})`} />;
        if (pend > 0) return <Pill tone="warn" label={`${acc}/${n}，${pend}待检`} />;
        return acc === n
          ? <Pill tone="ok" label={`${n} 合格`} />
          : <Pill tone="warn" label={`${acc}/${n}`} />;
      },
    },
    { header: '交货', render: (r) => (r.delivery_number ? <Pill tone="ok" label={r.delivery_status ?? '已交货'} /> : <span className="muted">未交货</span>) },
    {
      header: '关联',
      render: (r) => (r.pending_manual_link
        ? <Pill tone="bad" label="订单关联待核查" />
        : <Pill tone="ok" label="已关联" />),
    },
  ];

  return (
    <>
      {summary ? (
        <div className="kv-grid" style={{ marginBottom: 12 }}>
          <KeyValue k="送修工件">{num(summary.assets, 0)}</KeyValue>
          <KeyValue k="已检验">{num(summary.inspected, 0)}</KeyValue>
          <KeyValue k="已关联工单">{num(summary.assets - summary.without_work_order, 0)}</KeyValue>
          <KeyValue k="订单链待核查">{num(summary.pending_manual_link, 0)}</KeyValue>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
        <Field label="履历状态">
          <SelectInput value={cycleStatus} onChange={(e) => setCycleStatus(e.target.value)}>
            <option value="">全部</option>
            {Object.entries(CYCLE_STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </SelectInput>
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} />
          只看订单链待补齐
        </label>
      </div>

      <ListPage<RepairLedgerRow>
        title="送修工件台账"
        desc="客户受托工件唯一主台账 · QC 订单同步建立合同、工单与质检关联；交货单按已登记工件关联。无法修复时从已登记待退工件中选择退回，不进公司库存。"
        endpoint={endpoint}
        columns={columns}
        rowId={(r) => r.asset_id}
        showYear={false}
        searchPlaceholder="搜索工件序列号 / 内部编码 / 客户 / 合同号 / 工单号 / 物料"
        reloadSignal={reloadSignal}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost" onClick={() => { loadSummary(); setReloadSignal((n) => n + 1); }}>
              刷新
            </button>
          </div>
        }
        renderDetail={(row) => (
          <RowDetail row={row} canReturn={canReturn} onChanged={() => setReloadSignal((n) => n + 1)} />
        )}
      />
    </>
  );
}
